import { useState, useRef, useCallback, useEffect } from "react";
import { buildTutorSpeechSnapshot } from "@/features/tutor/utils/tutorSpeechSnapshot";
import { restorePageFocus } from "@/utils/audioUtils";
import { useStudentInputStore } from "@/features/tutor/store/useStudentInputStore";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore.js";
import { useUIStore } from "@/features/tutor/store/useUIStore.js";
import { useMicPermissionStore } from "@/features/tutor/store/useMicPermissionStore";
import {
  useNotepadStore,
  selectIsNotepadNonInteractive,
} from "@/features/notepad/store/useNotepadStore";
import { TUTOR_SESSION_PHASE_STATUS } from "@/features/tutor/constants";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes.js";
import {
  trackSpaceBarInteraction,
  SpaceBarInteractionTypes,
  trackStudentMessage,
} from "@/utils/mixpanel-utils";
import { showImageFailureNotification } from "@/utils/imageUploadNotifications";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useChatTrailStore } from "@/features/tutor/store/useChatTrailStore";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { v4 as uuidv4 } from "uuid";
import {
  TAP_THRESHOLD_MS,
  flushDrawLockBeforeSubmit,
  hasDrawLockInk,
} from "@/drawlock/drawLock";
import { RELEASABLE_SUBMISSION_SOURCES } from "@/features/tutor/vo-demo/ws/protocol";
import { canClaimGlobalKey, isTypingTarget } from "@/drawlock/guards";
import { useChatAvailability } from "@/features/tutor/hooks/useChatAvailability";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useHoldKey } from "@/drawlock/useHoldKey";

/**
 * Push-to-talk lives on S ("S" for EverTutor). The space bar now locks the
 * pad into a pen-down state so the student can write on a trackpad, so it can
 * no longer mean "talk".
 */
const TALK_KEY_CODE = "KeyS";
const ENTER_CODES = ["Enter", "NumpadEnter"];

/**
 * Is the student typing right now, on the tutor page specifically?
 *
 * The DOM half is `isTypingTarget` in drawlock/guards — shared with spaces and
 * deliberately store-free. The on-screen math keyboard is the one tutor-only
 * clause: it lives in a store and is not always reflected in the DOM.
 */
function isTypingHere(target) {
  if (isTypingTarget(target)) return true;
  // Math keyboard up: physical keys belong to it, so S must type, not talk.
  return useStudentInputStore.getState().isKeyboardOpen;
}

/** Enter inside an input box should submit, the same as an S tap. */
function isSlideInputBoxEnter(e) {
  return (
    canClaimGlobalKey(e, {
      codes: ENTER_CODES,
      respectActivatableTarget: false,
      isTyping: () => false,
    }) && Boolean(e.target?.matches?.("input[data-input-key]"))
  );
}

/**
 * Why the student's talk/submit input is unavailable, or `"ok"`.
 *
 * A reason rather than a boolean because callers differ: the key-down path
 * tells the student when the notepad is read-only, while the modal cases stay
 * silent. A boolean would either fire that notification during assessments or
 * drop it entirely.
 */
function studentInputBlockReason() {
  if (selectIsNotepadNonInteractive(useNotepadStore.getState())) {
    return "notepad-readonly";
  }
  const { assessments: aMap } = useAssessmentStore.getState();
  const { activeAssessmentModal: aModal, showExitLessonModal } =
    useUIStore.getState();
  if (
    aModal ||
    showExitLessonModal ||
    ASSESSMENT_KEYS.some((k) => aMap[k].isActive)
  ) {
    return "blocked-by-modal";
  }
  return "ok";
}

/**
 * Encapsulates all speak/submit button state and handlers for VoDemo.
 *
 * @param {React.MutableRefObject} params.pendingUploadedImageIdsByTurnRef -
 *   Map<turnId, string[]> of item IDs sent with each DISCUSS_CONTEXT, used to
 *   remove ghost attachments when the backend reports failed transcriptions.
 */
export function useSpeakButton({
  startRecording,
  stopRecording,
  armRecording,
  abortArmedRecording,
  retryMicPermission,
  cancelAllNotifications,
  emit,
  subscribe,
  handleInterrupt,
  setIsAITalking,
  setTutorSessionStatus,
  addLog,
  isAITalking,
  hasScratchpadPending,
  scratchpadCanvasExportRef,
  scratchpadImageItemsRef,
  scratchpadClearImageBytesRef,
  scratchpadRemoveImageByIdRef,
  pendingUploadedImageIdsByTurnRef,
  scratchpadResetPendingRef,
  markInputSubmitted,
  currentLessonId,
  speakbarExtraListeningTimeout,
  hasCanvasViewportChanged = false,
  isCanvasOpen = false,
}) {
  const { renderedMode } = useChatAvailability();
  const [isPressingToTalk, setIsPressingToTalk] = useState(false);
  const isPressingToTalkRef = useRef(false);
  const [isAwaitingTutorResponse, setIsAwaitingTutorResponse] = useState(false);
  // Arming: key down, audio buffering locally, UI shows "listening", but no
  // turn is open yet. Cleared when the press commits or the tap aborts.
  const [isArmingToTalk, setIsArmingToTalk] = useState(false);

  /**
   * The async press path (canvas export → DISCUSS_CONTEXT) is shared by the
   * keyboard wrapper and the pointer handlers. A release arriving mid-resolve
   * would hit the "not pressing" early-return and be swallowed, leaving the
   * mic open with nothing to close it. `useHoldKey` defers releases for the
   * keyboard; these refs do the same for the pointer path.
   */
  const pressInFlightRef = useRef(false);
  const releaseDeferredRef = useRef(false);

  const micPressTimeRef = useRef(0);
  const extraListeningTimeoutRef = useRef(null);
  const pendingScratchpadConfirmRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const hadPendingWorkOnPressRef = useRef(false);
  const isMountedRef = useRef(false);
  const midHoldDroppedRef = useRef(false);

  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);

  // Derived pending-work state: true if the last edited input has content not yet submitted
  const lastEditedInputKey = useStudentInputStore(
    (state) => state.lastEditedInputKey,
  );
  const lastEditedValue = useStudentInputStore((state) =>
    state.lastEditedInputKey
      ? state.inputs[state.lastEditedInputKey]
      : undefined,
  );
  const lastEditedSubmittedValue = useStudentInputStore((state) =>
    state.lastEditedInputKey
      ? state.submittedInputValues[state.lastEditedInputKey]
      : undefined,
  );

  const hasInputPending = !!(
    lastEditedInputKey &&
    lastEditedValue?.trim() &&
    lastEditedSubmittedValue !== lastEditedValue
  );
  const hasPendingWork = isCanvasOpen
    ? hasCanvasViewportChanged
    : hasScratchpadPending || hasInputPending;

  const buildDiscussPayload = useCallback(
    /**
     * `lockedInk` says the draw lock was holding a stroke when this press
     * started. That ink is committed by the flush below, but `hasScratchpadPending`
     * is several React hops downstream and still reads false for this press, so
     * the caller has to tell us rather than us waiting for a render.
     */
    async (voiceFollows, lockedInk = false, userMessage = "") => {
      const { inputMode, tutorAudioOn } = useChatSettingsStore.getState();
      const payload = {
        voice_follows: voiceFollows,
        input_mode: inputMode,
        tutor_audio_on: tutorAudioOn,
      };
      if (userMessage) payload.user_message = userMessage;
      let sentImageIds = null;
      let submittedInputKey = null;

      if (hasScratchpadPending || lockedInk) {
        const dataUrl = await scratchpadCanvasExportRef.current?.();
        if (dataUrl) {
          payload.scratchpad_png_b64 = dataUrl.split(",")[1];
          // Capture the confirm function now — its closure holds the current
          // snapshotKey, so it won't drift if the student draws more before START.
          pendingScratchpadConfirmRef.current =
            scratchpadResetPendingRef.current;
        }
        // Only the *first* submission after an upload ships the image bytes.
        // After that the backend holds the transcription, and this ref only
        // contains ghost icons (dataUrl cleared by scratchpadClearImageBytesRef).
        const items = scratchpadImageItemsRef?.current;
        const unsentItems = items?.filter((item) => !!item.dataUrl) ?? [];
        if (unsentItems.length > 0) {
          payload.scratchpad_images = unsentItems.map((item) => {
            const [header, b64] = item.dataUrl.split(",");
            const mime = header.match(/:(.*?);/)?.[1] ?? "image/jpeg";
            return { b64, mime_type: mime };
          });
          sentImageIds = unsentItems.map((item) => item.id);
          scratchpadClearImageBytesRef?.current?.();
        }
      }

      const { inputs, submittedInputValues, lastEditedInputKey, getActionId } =
        useStudentInputStore.getState();
      if (lastEditedInputKey) {
        const value = inputs[lastEditedInputKey];
        const actionId = getActionId(lastEditedInputKey);
        if (
          value?.trim() &&
          actionId &&
          value !== submittedInputValues[lastEditedInputKey]
        ) {
          payload.inputs = { [actionId]: value };
          markInputSubmitted(lastEditedInputKey, value);
          submittedInputKey = lastEditedInputKey;
        }
      }

      return { payload, sentImageIds, submittedInputKey };
    },
    [
      hasScratchpadPending,
      scratchpadCanvasExportRef,
      scratchpadImageItemsRef,
      scratchpadClearImageBytesRef,
      scratchpadResetPendingRef,
      markInputSubmitted,
    ],
  );

  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe("UPLOADED_IMAGES_STATUS", ({ failedImages, turnId }) => {
      const ids = pendingUploadedImageIdsByTurnRef?.current?.get(turnId);
      pendingUploadedImageIdsByTurnRef?.current?.delete(turnId);
      if (!Array.isArray(failedImages) || failedImages.length === 0) return;
      if (Array.isArray(ids)) {
        const remove = scratchpadRemoveImageByIdRef?.current;
        if (remove) {
          for (const { index } of failedImages) {
            const id = ids[index];
            if (id) remove(id);
          }
        }
      }
      const qualityCount = failedImages.filter(
        (f) => f.reason === "quality",
      ).length;
      const errorCount = failedImages.filter(
        (f) => f.reason === "error",
      ).length;
      showImageFailureNotification(qualityCount, errorCount);
    });
  }, [
    subscribe,
    pendingUploadedImageIdsByTurnRef,
    scratchpadRemoveImageByIdRef,
  ]);

  // The backend never produced a verdict for part of a submission: lift the optimistic
  // marks it names so the student can send that work again. The signal is addressed to
  // one submit_id and names its sources — releasing anything else re-opens the
  // attempt-counter and scratchpad-resubmit regressions.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe("DISCUSS_CONTEXT_FAILED", (failure) => {
      const submitId = failure?.submitId ?? null;
      const released = failure?.released ?? [];
      const pending = pendingPayloadRef.current;

      if (!submitId) {
        // An older backend, which sent this signal for one reason only: the scratchpad
        // read failed. Keep the pre-address behaviour rather than doing less than before
        // — without this the following START confirms a snapshot the student cannot
        // resubmit. Reachable during a deploy window or a backend rollback.
        pendingScratchpadConfirmRef.current = null;
        setIsAwaitingTutorResponse(false);
        addLog(
          "[useSpeakButton] DISCUSS_CONTEXT_FAILED without submit_id — discarding scratchpad confirm and unblocking",
        );
        return;
      }
      if (pending && pending.payload.submit_id !== submitId) {
        // A signal for an older submission while a newer one is in flight. Unblocking
        // here would let the student submit on top of work that is still pending.
        addLog(
          `[useSpeakButton] DISCUSS_CONTEXT_FAILED for submit_id=${submitId} but in-flight is ${pending.payload.submit_id} — leaving the button blocked`,
        );
        return;
      }

      // Nothing in flight, or this signal is for it: either way the student is free.
      setIsAwaitingTutorResponse(false);

      if (!pending) {
        addLog(
          `[useSpeakButton] DISCUSS_CONTEXT_FAILED for submit_id=${submitId} with nothing in flight — unblocking only`,
        );
        return;
      }

      let wasAnythingReleased = false;
      if (released.includes(RELEASABLE_SUBMISSION_SOURCES.SCRATCHPAD)) {
        // Prevent the START handler from calling confirmFn so hasScratchpadPending stays true.
        pendingScratchpadConfirmRef.current = null;
        wasAnythingReleased = true;
      }
      if (
        released.includes(RELEASABLE_SUBMISSION_SOURCES.INPUTS) &&
        pending.inputKey
      ) {
        useStudentInputStore.getState().unmarkInputSubmitted(pending.inputKey);
        wasAnythingReleased = true;
      }
      addLog(
        wasAnythingReleased
          ? `[useSpeakButton] DISCUSS_CONTEXT_FAILED — released ${released.join(",")} for submit_id=${submitId}`
          : `[useSpeakButton] DISCUSS_CONTEXT_FAILED — nothing to release for submit_id=${submitId} (released=${released.join(",") || "none"})`,
      );
    });
  }, [subscribe, addLog]);

  // Confirm scratchpad submission when backend acknowledges with START.
  // Also clears pendingPayloadRef so a subsequent WS_RECONNECTED does not resubmit.
  // Uses the function captured at submission time so it marks the correct
  // snapshot as submitted, even if the student has drawn more since then.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe("START", (turnStart) => {
      const startSubmitId = turnStart?.submitId ?? null;
      const pending = pendingPayloadRef.current;

      // Only the submission this START names may be retired. Every turn raised by a
      // submission carries the id — the tap path, all three DONE branches, and the
      // reconnect resubmit that re-enters the tap path — so an id-less START belongs
      // to something else: a voice turn, an inactivity nudge, the intro. Retiring on
      // one of those leaves the pending submission's release signal with nothing to
      // match, which is the bug this address exists to close.
      if (pending && pending.payload.submit_id !== startSubmitId) {
        addLog(
          `[useSpeakButton] START for submit_id=${startSubmitId ?? "none"} — in-flight is ${pending.payload.submit_id}, keeping it`,
        );
        return;
      }

      addLog(
        `[useSpeakButton] START — backend ACK received (retiring submit_id=${pending?.payload.submit_id ?? "none"})`,
      );
      pendingPayloadRef.current = null;
      const confirmFn = pendingScratchpadConfirmRef.current;
      if (confirmFn) {
        pendingScratchpadConfirmRef.current = null;
        confirmFn();
      }
    });
  }, [subscribe, addLog]);

  // On WS reconnect: if a submission is in-flight (sent but no START yet),
  // resubmit immediately. Backend deduplication guards against double-processing
  // if the original was already received — it will silently ignore the retransmit.
  // If the original was lost in transit, this ensures the backend gets it.
  useEffect(() => {
    if (!subscribe) return undefined;
    const unsubReconnect = subscribe("WS_RECONNECTED", () => {
      // Mic send lock is cleared by useWebsocketClient before emitting
      // WS_RECONNECTED, so no explicit unlock is needed here.
      // If the student was mid-hold when WS dropped (no in-flight submission),
      // send DONE so the backend finalizes STT with whatever audio it received
      // before the drop. Backend handles both outcomes: if it captured audio it
      // responds normally; if it got nothing it plays the misclick canned message.
      if (midHoldDroppedRef.current) {
        midHoldDroppedRef.current = false;
        addLog(
          "[useSpeakButton] WS_RECONNECTED — mid-hold was dropped; sending DONE to finalize audio backend received",
        );
        emit("USER_AUDIO_DONE");
      }
      if (!pendingPayloadRef.current) {
        addLog("[useSpeakButton] WS_RECONNECTED — no pending payload");
        return;
      }
      // Resubmit with voice_follows: false — if the student was mid-hold when WS
      // dropped, the WS_DISCONNECTED handler already stopped recording and reset
      // isPressingToTalkRef, so there is no live audio stream to wait on.
      const resubmitPayload = {
        ...pendingPayloadRef.current.payload,
        voice_follows: false,
      };
      addLog(
        "[DISCUSS_CONTEXT] WS reconnected with in-flight submission — resubmitting submit_id=" +
          resubmitPayload.submit_id,
      );
      setIsAwaitingTutorResponse(true);
      emit("DISCUSS_CONTEXT", JSON.stringify(resubmitPayload));
    });
    return unsubReconnect;
  }, [subscribe, emit, addLog]);

  // On RESET: backend has wiped the session, so any in-flight submission is
  // definitively lost. Clear refs to prevent WS_RECONNECTED from resubmitting
  // a stale payload against the fresh session, and unblock the button.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe("RESET", () => {
      addLog(
        "[useSpeakButton] RESET — session wiped; clearing in-flight refs and unblocking button",
      );
      pendingPayloadRef.current = null;
      pendingScratchpadConfirmRef.current = null;
      setIsAwaitingTutorResponse(false);
    });
  }, [subscribe, addLog]);

  // On WS disconnect mid-hold: stop recording regardless of whether there is an
  // in-flight submission. For plain hold-to-speak, stopping prevents the worklet
  // from emitting USER_AUDIO_DONE on reconnect — a stale DONE on a fresh
  // connection with no preceding TURN_CHANGE leaves the backend hanging.
  // For the scratchpad case, stopping prevents a double LLM call on reconnect.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe("WS_DISCONNECTED", () => {
      addLog(
        `[useSpeakButton] WS_DISCONNECTED — t=${Date.now()}, isPressingToTalk=${isPressingToTalkRef.current}, extraListeningActive=${!!extraListeningTimeoutRef.current}, pendingPayload=${!!pendingPayloadRef.current}, midHoldDropped=${midHoldDroppedRef.current}`,
      );
      // If the extra-listening timeout is pending (student released just before
      // the WS dropped), cancel it now. The DONE chunk won't make it to the
      // backend, so showing LOADING would leave the UI stuck indefinitely.
      if (extraListeningTimeoutRef.current) {
        clearTimeout(extraListeningTimeoutRef.current);
        extraListeningTimeoutRef.current = null;
        // Post-release drop: student finished speaking. If DONE reached the backend
        // before the WS dropped, the response will arrive normally after reconnect
        // (turn_id unchanged). If DONE was lost, the backend is stuck but the student
        // pressing S again will emit TURN_CHANGE to unstick it. Either way,
        // no action needed here — match production behaviour.
        if (!isPressingToTalkRef.current) {
          addLog(
            "[useSpeakButton] WS_DISCONNECTED — extra-listening timeout cleared after release; no reconnect action needed (post-release drop)",
          );
        }
      }
      if (!isPressingToTalkRef.current) return;
      // Structural enforcement of mutual exclusivity: midHoldDropped is set only
      // when there is no in-flight submission. These two paths can never both be
      // true — WS_DISCONNECTED only sets midHoldDropped=true when pendingPayload
      // is null. If both refs were set, WS_RECONNECTED would fire DONE *and*
      // resubmit DISCUSS_CONTEXT, causing a double LLM call.
      midHoldDroppedRef.current = !pendingPayloadRef.current;
      if (pendingPayloadRef.current) {
        addLog(
          "[useSpeakButton] WS_DISCONNECTED — in-flight submission + hold; stopping recording (submit_id=" +
            pendingPayloadRef.current.payload.submit_id +
            ")",
        );
      } else {
        addLog(
          "[useSpeakButton] WS_DISCONNECTED — mid-hold active; stopping recording; will send DONE on reconnect to finalize audio backend received",
        );
      }
      // Mic send lock is set by useWebsocketClient before emitting WS_DISCONNECTED,
      // so no explicit LOCK_MIC_SEND emit is needed here.
      isPressingToTalkRef.current = false;
      setIsPressingToTalk(false);
      stopRecording(true);
    });
  }, [subscribe, stopRecording, addLog]);

  // On slide change: drop any in-flight submission so WS_RECONNECTED does not
  // resubmit a stale payload from the previous slide to the new slide's tutor.
  // Also clears the scratchpad confirm ref (new slide has a fresh scratchpad) and
  // unblocks the button (no pending submission on the new slide).
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (pendingPayloadRef.current) {
      addLog(
        "[useSpeakButton] slide change — dropping in-flight pendingPayloadRef to prevent stale resubmit (submit_id=" +
          pendingPayloadRef.current.payload.submit_id +
          ")",
      );
    }
    pendingPayloadRef.current = null;
    pendingScratchpadConfirmRef.current = null;
    midHoldDroppedRef.current = false;
    setIsAwaitingTutorResponse(false);
  }, [currentSlideIndex, addLog]);

  // Unblock the button as soon as the submission's context has been sent —
  // hasPendingWork flips false when the canvas screenshot part is captured
  // (ScreenshotPartUtil) or the backend START ack confirms the scratchpad
  // snapshot. Waiting for TTS audio (the AUDIO_CHUNK fallback) would keep
  // "Submitted" up for the whole response and block interrupts. Ordering-safe:
  // runs whether pending work resolves before or after the release that sets
  // isAwaitingTutorResponse.
  useEffect(() => {
    if (isAwaitingTutorResponse && !hasPendingWork) {
      setIsAwaitingTutorResponse(false);
    }
  }, [isAwaitingTutorResponse, hasPendingWork]);

  const handleKeyUp = useCallback(
    (e) => {
      const isTalkKey = e.code === TALK_KEY_CODE;
      const isPointerOrTouch =
        e.type === "pointerup" ||
        e.type === "pointercancel" ||
        e.type === "lostpointercapture";

      if (isTalkKey || isPointerOrTouch) {
        if (!isPressingToTalkRef.current) return;
        if (isTalkKey) {
          addLog("Talk key (S) released");
          e.preventDefault?.();
        }

        isPressingToTalkRef.current = false;
        setIsPressingToTalk(false);

        const pressDuration = Date.now() - micPressTimeRef.current;
        if (pressDuration >= 200) {
          if (window.animationController != null) {
            window.animationController.call("IDLE");
          }
        }

        if (isCanvasOpen) {
          // Anchor for notepad button_release → first_audio metric.
          // Consumed by useCanvasRuntimeBridge on the first CANVAS_AUDIO
          // chunk, then cleared. Only stamped when the canvas is the target
          // surface so the tutor-side path can't collide.
          window.__notepadButtonReleaseWallClockMs = Date.now();
        }

        stopRecording();

        if (extraListeningTimeoutRef.current) {
          clearTimeout(extraListeningTimeoutRef.current);
          extraListeningTimeoutRef.current = null;
        }

        extraListeningTimeoutRef.current = setTimeout(() => {
          if (!useWebsocketStore.getState().wsConnected) {
            addLog(
              "[useSpeakButton] extraListeningTimeout: WS disconnected — skipping LOADING",
            );
            return;
          }
          setIsAITalking(true);
          setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.LOADING);
          trackStudentMessage({
            lesson_id: currentLessonId,
            type: "audio_input",
          });
        }, speakbarExtraListeningTimeout);
      }
    },
    [
      addLog,
      stopRecording,
      setIsAITalking,
      setTutorSessionStatus,
      speakbarExtraListeningTimeout,
      currentLessonId,
      isCanvasOpen,
    ],
  );

  const checkMicAvailable = useCallback(() => {
    if (useMicPermissionStore.getState().micUnavailable) {
      retryMicPermission();
      return false;
    }
    return true;
  }, [retryMicPermission]);

  const handleKeyDown = useCallback(
    (e) => {
      const isTalkKey = e.code === TALK_KEY_CODE;
      const isMouseOrTouch = e.type === "pointerdown";

      if ((isTalkKey && !e.repeat) || isMouseOrTouch) {
        if (isPressingToTalkRef.current) return;
        if (!checkMicAvailable()) return;
        // Block mic when notepad is in non-interactive mode (student-review
        // or AI-ingress on a historical page)
        const blocked = studentInputBlockReason();
        if (blocked === "notepad-readonly") {
          // Tell the student why: a historical/AI-owned page can't take voice.
          window.dispatchEvent(new CustomEvent("notepad:student-talk-blocked"));
          return;
        }
        if (blocked !== "ok") return;
        // Before cancelAllNotifications() clears the countdown it has to report.
        const speechSnapshot = buildTutorSpeechSnapshot();
        cancelAllNotifications();

        if (extraListeningTimeoutRef.current) {
          clearTimeout(extraListeningTimeoutRef.current);
          extraListeningTimeoutRef.current = null;
        }

        isPressingToTalkRef.current = true;
        setIsPressingToTalk(true);
        micPressTimeRef.current = Date.now();
        startRecording(speechSnapshot);
        window.dispatchEvent(new CustomEvent("notepad:cancel-close"));
        if (window.animationController != null) {
          window.animationController.forceListening();
        }
        // NOTE: the event is still named "space bar" for dashboard continuity,
        // but push-to-talk now lives on S. Rename the Mixpanel event and this
        // helper together if/when the historical series can be broken.
        if (isAITalking) {
          trackSpaceBarInteraction({
            type: SpaceBarInteractionTypes.INTERRUPTS,
          });
        } else {
          trackSpaceBarInteraction({ type: SpaceBarInteractionTypes.FOLLOW });
        }
      }
    },
    [cancelAllNotifications, checkMicAvailable, startRecording, isAITalking],
  );

  const performUnifiedButtonRelease = useCallback(
    (e) => {
      handleKeyUp(e);
      if (hadPendingWorkOnPressRef.current) {
        hadPendingWorkOnPressRef.current = false;
        setIsAwaitingTutorResponse(true);
      }
    },
    [handleKeyUp],
  );

  const handleUnifiedButtonDown = useCallback(
    async (e) => {
      if (isAwaitingTutorResponse) return;

      // Asked before the guards and before any commit: a stroke the lock is
      // still holding is pending work, but committing it is a state update this
      // press will never see.
      const lockedInk = hasDrawLockInk();

      if (!hasPendingWork && !lockedInk) {
        handleKeyDown(e);
        return;
      }

      if (isPressingToTalkRef.current) return;
      if (pressInFlightRef.current) return;
      if (!checkMicAvailable()) return;
      if (!useWebsocketStore.getState().wsConnected) return;

      // Only now that the press is definitely going ahead. Committing ink is
      // irreversible, so it must not happen for a press that gets refused above.
      flushDrawLockBeforeSubmit();

      pressInFlightRef.current = true;
      try {
        hadPendingWorkOnPressRef.current = true;
        const { payload, sentImageIds, submittedInputKey } =
          await buildDiscussPayload(true, lockedInk);

        // Before handleInterrupt(): it clears the PCM player synchronously.
        // See utils/tutorSpeechSnapshot.js.
        const speechSnapshot = buildTutorSpeechSnapshot();

        handleInterrupt();
        isPressingToTalkRef.current = true;
        setIsPressingToTalk(true);
        micPressTimeRef.current = Date.now();
        startRecording(speechSnapshot);

        // startRecording() synchronously emits TURN_CHANGE; capture ids under
        // the new turn_id so UPLOADED_IMAGES_STATUS can map back to the ghost
        // attachments on failure.
        if (sentImageIds && sentImageIds.length > 0) {
          const currentTurnId = useTurnStore.getState().getTurnId();
          if (currentTurnId && pendingUploadedImageIdsByTurnRef?.current) {
            pendingUploadedImageIdsByTurnRef.current.set(
              currentTurnId,
              sentImageIds,
            );
          }
        }
        window.dispatchEvent(new CustomEvent("notepad:cancel-close"));
        // Stable ID for this submission — survives WS reconnect so the backend
        // can deduplicate if the frontend retransmits the same payload.
        payload.submit_id = uuidv4();
        // Cache payload — if WS dies before START, WS_RECONNECTED handler resubmits it.
        // Cleared by START handler once backend acknowledges. The input key rides along
        // in the same object so a DISCUSS_CONTEXT_FAILED can never release the wrong one.
        pendingPayloadRef.current = { payload, inputKey: submittedInputKey };
        emit("DISCUSS_CONTEXT", JSON.stringify(payload));
        if (window.animationController)
          window.animationController.forceListening();
      } finally {
        pressInFlightRef.current = false;
        // A release (pointer-up, cancel, lost capture) arrived while the press
        // was resolving and was deferred — apply it now so the mic closes.
        if (releaseDeferredRef.current) {
          releaseDeferredRef.current = false;
          performUnifiedButtonRelease({
            type: "pointerup",
            preventDefault: () => {},
          });
        }
      }
    },
    [
      isAwaitingTutorResponse,
      hasPendingWork,
      handleKeyDown,
      buildDiscussPayload,
      handleInterrupt,
      emit,
      startRecording,
      checkMicAvailable,
      pendingUploadedImageIdsByTurnRef,
      performUnifiedButtonRelease,
    ],
  );

  const handleUnifiedButtonUp = useCallback(
    (e) => {
      // Press still resolving (canvas export in flight) — record the release
      // and let the continuation apply it, or the mic stays open.
      if (pressInFlightRef.current) {
        releaseDeferredRef.current = true;
        return;
      }
      performUnifiedButtonRelease(e);
    },
    [performUnifiedButtonRelease],
  );

  /**
   * Explicit, silent submission — a tap of the talk key or Enter while there
   * is pending work. Unlike the hold path this never opens the mic, so
   * `voice_follows` is false and no recording turn is started.
   */
  const submitInFlightRef = useRef(false);

  const runSubmit = useCallback(
    async (userMessage) => {
      // A stroke the lock still holds counts as pending work even though the
      // commit that would make `hasPendingWork` true cannot land in this tick.
      const lockedInk = hasDrawLockInk();
      if (!hasPendingWork && !lockedInk) return false;
      if (isPressingToTalkRef.current) return false;
      if (!useWebsocketStore.getState().wsConnected) return false;

      // Same gating as the hold path, but silent — only key-down notifies.
      if (studentInputBlockReason() !== "ok") return false;

      // Commit only once the submission is certain — every refusal above must
      // leave the student's ink exactly as it was.
      flushDrawLockBeforeSubmit();

      // Notepad ink travels in the canvas prompt context, never via
      // buildDiscussPayload — so a DISCUSS_CONTEXT here would emit an empty
      // payload and no response would come back. Its turn runs on the mic
      // lifecycle instead: TURN_CHANGE{source:"mic"} on press, USER_AUDIO_DONE
      // on release. So a tap replays what the "Tap to Submit" button always did,
      // press then release, rather than inventing a second submit path.
      if (isCanvasOpen) {
        // The canvas turn runs on the mic lifecycle and carries no free text, so
        // a typed sentence would be dropped while the caller was told it sent.
        if (userMessage) return false;
        await handleUnifiedButtonDown({
          code: TALK_KEY_CODE,
          type: "keydown",
          repeat: false,
          preventDefault: () => {},
        });
        handleUnifiedButtonUp({
          code: TALK_KEY_CODE,
          type: "keyup",
          preventDefault: () => {},
        });
        if (renderedMode === "chat") {
          useChatTrailStore.getState().beginTutorTurn();
        }
        return true;
      }

      const { payload, sentImageIds, submittedInputKey } =
        await buildDiscussPayload(false, lockedInk, userMessage);

      // Belt and braces: never emit a context-free payload, whatever the reason.
      // Locking the button on a submission the backend will discard is the
      // failure mode this guard exists to prevent.
      const hasContext =
        !!payload.scratchpad_png_b64 ||
        (payload.scratchpad_images?.length ?? 0) > 0 ||
        !!payload.inputs ||
        !!payload.user_message;
      if (!hasContext) {
        addLog(
          "[useSpeakButton] silent submit produced no context — not emitting",
        );
        return false;
      }

      // Before handleInterrupt(): it clears the PCM player synchronously.
      // See utils/tutorSpeechSnapshot.js.
      const speechSnapshot = buildTutorSpeechSnapshot();

      handleInterrupt();

      // A submission is an interrupt like any other student action: it replaces what the
      // student is asking about. Without a turn of its own, the previous submission's
      // answer plays to the end and this one is answered behind it — the student hears the
      // tutor discuss an answer they have already corrected. The hold path gets this from
      // startRecording(); a tap starts no recording, so it announces the turn itself.
      emit("TURN_CHANGE", speechSnapshot);

      // Capture ids under the new turn_id so UPLOADED_IMAGES_STATUS can map back to the
      // ghost attachments on failure.
      if (sentImageIds && sentImageIds.length > 0) {
        const currentTurnId = useTurnStore.getState().getTurnId();
        if (currentTurnId && pendingUploadedImageIdsByTurnRef?.current) {
          pendingUploadedImageIdsByTurnRef.current.set(
            currentTurnId,
            sentImageIds,
          );
        }
      }

      window.dispatchEvent(new CustomEvent("notepad:cancel-close"));
      payload.submit_id = uuidv4();
      pendingPayloadRef.current = { payload, inputKey: submittedInputKey };
      emit("DISCUSS_CONTEXT", JSON.stringify(payload));
      setIsAwaitingTutorResponse(true);
      return true;
    },
    [
      hasPendingWork,
      isCanvasOpen,
      renderedMode,
      handleUnifiedButtonDown,
      handleUnifiedButtonUp,
      buildDiscussPayload,
      handleInterrupt,
      emit,
      addLog,
      pendingUploadedImageIdsByTurnRef,
    ],
  );

  const submitPendingWork = useCallback(
    async ({ userMessage = "" } = {}) => {
      // Set synchronously: isAwaitingTutorResponse is only raised after the
      // canvas export below, so two sends inside that window would both pass and
      // emit two submissions with distinct submit_ids, which the backend dedup
      // cannot collapse.
      if (submitInFlightRef.current) return false;
      if (isAwaitingTutorResponse) return false;
      submitInFlightRef.current = true;
      try {
        return await runSubmit(userMessage);
      } finally {
        submitInFlightRef.current = false;
      }
    },
    [runSubmit, isAwaitingTutorResponse],
  );

  /**
   * Optimistic capture on key-down: flip the UI to "listening" and buffer mic
   * audio locally before tap-vs-hold is known, so the first syllable isn't
   * lost. Only reversible things happen here — the turn itself still waits for
   * the threshold, and a tap discards the buffer via abortOptimisticArm.
   */
  const armOptimistically = useCallback(() => {
    if (!armRecording) return false;
    if (isAwaitingTutorResponse) return false;
    if (isPressingToTalkRef.current) return false;
    if (useMicPermissionStore.getState().micUnavailable) return false;
    // Read-only mirror of the press path's gate: if the press would be refused
    // at the threshold, don't flash "listening" now. Silent — the key-down path
    // owns the student-facing notification.
    if (studentInputBlockReason() !== "ok") return false;
    setIsArmingToTalk(true);
    armRecording();
    if (window.animationController != null) {
      window.animationController.forceListening();
    }
    return true;
  }, [armRecording, isAwaitingTutorResponse]);

  /** The press never happened (tap, abandon, or a rejected press) — discard
   * the pre-roll and revert the optimistic UI. */
  const abortOptimisticArm = useCallback(() => {
    setIsArmingToTalk(false);
    abortArmedRecording?.();
    if (window.animationController != null) {
      window.animationController.call("IDLE");
    }
  }, [abortArmedRecording]);

  /**
   * Stable refs so the mount-once listeners never hold a stale callback.
   *
   * Assigned during render rather than in effects: an effect leaves the ref
   * pointing at the PREVIOUS callback for the window between render and commit,
   * so anything reading it in that window gets the old one. Matches how
   * useHoldKey and useTldrawDrawLock do it.
   */
  const handleUnifiedButtonDownRef = useRef(handleUnifiedButtonDown);
  const handleUnifiedButtonUpRef = useRef(handleUnifiedButtonUp);
  const submitPendingWorkRef = useRef(submitPendingWork);
  const armOptimisticallyRef = useRef(armOptimistically);
  const abortOptimisticArmRef = useRef(abortOptimisticArm);
  handleUnifiedButtonDownRef.current = handleUnifiedButtonDown;
  handleUnifiedButtonUpRef.current = handleUnifiedButtonUp;
  submitPendingWorkRef.current = submitPendingWork;
  armOptimisticallyRef.current = armOptimistically;
  abortOptimisticArmRef.current = abortOptimisticArm;

  /** Silent submit. The double-fire guard lives in submitPendingWork itself,
   * so the typed-chat caller is covered by it too. */
  const runSubmitRef = useRef(() => {});
  runSubmitRef.current = () => {
    Promise.resolve(submitPendingWorkRef.current?.()).catch(() => {});
  };

  /**
   * Unmount safety for the POINTER path. The keyboard path is `useHoldKey`'s
   * job, but the button's pointer handlers are called directly, and an async
   * press continuation can still land after this component is gone — opening
   * the mic with no handler left to close it. Leave a deferred release behind
   * so the continuation closes it itself.
   */
  useEffect(
    () => () => {
      if (pressInFlightRef.current) releaseDeferredRef.current = true;
    },
    [],
  );

  // The Enter-to-submit listener. Enter has no tap-vs-hold ambiguity, so it is
  // a plain listener rather than part of the hold machine below.
  useEffect(() => {
    // In chat mode the composer owns Enter. Left global it reaches runSubmit,
    // whose canvas branch replays the mic lifecycle — so Enter anywhere outside
    // a text field opened a voice turn on a surface that has no talk button.
    if (renderedMode === "chat") return undefined;

    const onKeyDown = (e) => {
      if (e.repeat) return;
      // Leaves Enter to focused buttons/links and to the cached-bite prompt.
      if (
        !canClaimGlobalKey(e, { codes: ENTER_CODES, isTyping: isTypingHere })
      ) {
        return;
      }
      runSubmitRef.current();
    };
    const onSlideInputBoxEnter = (e) => {
      if (e.repeat || !isSlideInputBoxEnter(e)) return;
      runSubmitRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keydown", onSlideInputBoxEnter, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keydown", onSlideInputBoxEnter, true);
    };
  }, [renderedMode]);

  // Safari steals page focus in a few situations (mic permission revoked, the
  // getUserMedia dialog closing); reclaim it so window.keydown still fires.
  useEffect(() => {
    const onFocus = () => {
      if (document.activeElement === document.body || !document.activeElement) {
        restorePageFocus();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  /**
   * Push-to-talk on hold S, tap-to-submit on tap S.
   *
   * Press bookkeeping — tap-vs-hold, abandon paths, deferred releases — lives
   * in the shared `useHoldKey` machine. Tutor-specific is only what happens at
   * each hook point: the press may await a canvas export, a tap submits
   * pending work.
   */
  useHoldKey({
    code: TALK_KEY_CODE,
    thresholdMs: TAP_THRESHOLD_MS,
    enabled: renderedMode !== "chat",
    isTyping: isTypingHere,
    // No flush here. Arming is reversible and happens before the press is
    // known to be allowed — committing a stroke here would end draw mode even
    // for a press the guards go on to refuse. The press path asks
    // hasDrawLockInk() instead, so it no longer needs a render to have landed.
    onArm: () => armOptimisticallyRef.current(),
    onAbortArm: () => abortOptimisticArmRef.current(),
    // The pre-roll was carried into a live turn; the real pressing state owns
    // the UI from here.
    onArmCommitted: () => setIsArmingToTalk(false),
    onHoldStart: () =>
      Promise.resolve(
        handleUnifiedButtonDownRef.current({
          code: TALK_KEY_CODE,
          type: "keydown",
          repeat: false,
          preventDefault: () => {},
        }),
      ).then(() =>
        // The press can be refused (a modal opened mid-press, WS down). Report
        // that as a failed hold so no release is expected and the armed
        // capture is discarded rather than left running.
        isPressingToTalkRef.current ? true : false,
      ),
    onHoldEnd: () =>
      handleUnifiedButtonUpRef.current({
        code: TALK_KEY_CODE,
        type: "keyup",
        preventDefault: () => {},
      }),
    onTap: () => runSubmitRef.current(),
  });

  return {
    isPressingToTalk,
    isArmingToTalk,
    isAwaitingTutorResponse,
    setIsAwaitingTutorResponse,
    hasPendingWork,
    extraListeningTimeoutRef,
    micPressTimeRef,
    handleUnifiedButtonDown,
    handleUnifiedButtonUp,
    submitPendingWork,
  };
}
